/**
 * Request Overview Section Component
 *
 * @module @features/requests/components/RequestDetails/components/RequestOverview
 */

import React from 'react';
import { Badge } from '@dynamon-as/ui';
import { AccessTime, Event, Today } from '@mui/icons-material';
import { useI18n, useRequestTranslations } from '@shared/i18n';
import { formatDate, formatDateRange } from '@features/requests/utils';
import type { Request } from '@features/requests/types';
import { RequestStatus } from '@features/requests/types';
import { STATUS_COLORS } from '../../../constants';
import styles from '../RequestDetails.module.css';

/**
 * Props for RequestOverview component
 */
export interface RequestOverviewProps {
    request: Request;
}

/**
 * Displays request overview information
 */
export function RequestOverview({
    request,
}: RequestOverviewProps): React.ReactElement {
    const { t } = useI18n();
    const { getStatusLabel } = useRequestTranslations();

    const statusColor = STATUS_COLORS[request.status] ?? 'neutral';
    const statusLabel = getStatusLabel(request.status);
    const hasDeadline = !!request.deadline;

    const dateRange =
        request.status === RequestStatus.DRAFT && request.deadline
            ? formatDate(request.deadline)
            : (formatDateRange(request.startDate, request.endDate) ??
              t('requests.noDates'));

    return (
        <div className={styles.overviewSection}>
            <div className={styles.overviewGrid}>
                {hasDeadline && request.deadline && (
                    <div className={styles.overviewItem}>
                        <div className={styles.overviewIcon}>
                            <AccessTime fontSize="small" />
                        </div>
                        <div className={styles.overviewContent}>
                            <span className={styles.overviewLabel}>
                                {t('requests.details.deadline')}
                            </span>
                            <span className={styles.overviewValue}>
                                {formatDate(request.deadline)}
                            </span>
                        </div>
                    </div>
                )}

                <div className={styles.overviewItem}>
                    <div className={styles.overviewIcon}>
                        <Today fontSize="small" />
                    </div>
                    <div className={styles.overviewContent}>
                        <span className={styles.overviewLabel}>
                            {t('requests.details.status')}
                        </span>
                        <Badge
                            text={statusLabel}
                            variant={statusColor}
                            size="small"
                        />
                    </div>
                </div>

                <div className={styles.overviewItem}>
                    <div className={styles.overviewIcon}>
                        <Event fontSize="small" />
                    </div>
                    <div className={styles.overviewContent}>
                        <span className={styles.overviewLabel}>
                            {t('requests.details.period')}
                        </span>
                        <span className={styles.overviewValue}>
                            {dateRange}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
